import { createContext, useContext, useState, type ReactNode } from 'react';

type ModalType = 'alert' | 'confirm';

interface ModalState {
  open: boolean;
  type: ModalType;
  message: string;
  onOk?: () => void;
  onCancel?: () => void;
}

interface ModalContextType {
  showAlert: (msg: string) => void;
  showConfirm: (msg: string) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modal, setModal] = useState<ModalState>({
    open: false,
    type: 'alert',
    message: '',
  });

  function showAlert(msg: string) {
    return new Promise<void>((resolve) => {
      setModal({
        open: true,
        type: 'alert',
        message: msg,
        onOk: () => {
          setModal(prev => ({ ...prev, open: false }));
          resolve();
        },
      });
    });
  }

  function showConfirm(msg: string): Promise<boolean> {
    return new Promise((resolve) => {
      setModal({
        open: true,
        type: 'confirm',
        message: msg,
        onOk: () => {
          setModal(prev => ({ ...prev, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setModal(prev => ({ ...prev, open: false }));
          resolve(false);
        },
      });
    });
  }

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {modal.open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-bold mb-4">iSentinel360</h2>
            <p className="text-sm text-muted-foreground mb-6">{modal.message}</p>
            <div className="flex justify-end gap-2">
              {modal.type === 'confirm' && (
                <button
                  className="px-4 py-2 text-sm rounded-md border hover:bg-accent"
                  onClick={modal.onCancel}
                >
                  Cancel
                </button>
              )}
              <button
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={modal.onOk}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used inside ModalProvider');
  return ctx;
}